import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Package, ShoppingBag, Sprout, UserCheck } from "lucide-react";
import { api, fmtEur } from "../../api";
import PageHeader from "../../components/PageHeader";
import { Badge } from "../../components/ui/badge";

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);

  if (!d) return <div className="p-6 text-[#5C665A]" data-testid="dashboard-loading">Loading…</div>;

  const kpis = [
    { key: "pending_buyers", label: "Pending buyers", icon: UserCheck, to: "/admin/buyers", testid: "kpi-pending-buyers" },
    { key: "pending_orders", label: "Pending orders", icon: ShoppingBag, to: "/admin/orders", testid: "kpi-pending-orders" },
    { key: "low_stock_variants", label: "Low stock (≤5)", icon: AlertTriangle, to: "/admin/stock?low=1", testid: "kpi-low-stock" },
    { key: "published_products", label: "Products published", icon: Sprout, to: "/admin/products", testid: "kpi-products" },
  ];

  return (
    <>
      <PageHeader title="Dashboard" subtitle="At-a-glance nursery activity." testid="page-dashboard" />
      <div className="p-6 max-w-6xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {kpis.map((k) => (
            <Link key={k.key} to={k.to} className="group bg-white border border-[#E2E4E0] rounded-lg p-4 hover:border-[#2D6A4F] transition-colors" data-testid={k.testid}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#5C665A] db-h font-semibold uppercase tracking-wider">{k.label}</span>
                <k.icon size={16} className="text-[#5C665A]" />
              </div>
              <div className="mt-2 db-h text-3xl font-bold" data-testid={`${k.testid}-value`}>{d.counts[k.key]}</div>
              <div className="mt-1 text-xs text-[#2D6A4F] inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">Open <ArrowRight size={12} /></div>
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="bg-white border border-[#E2E4E0] rounded-lg">
            <header className="px-4 py-3 border-b border-[#E2E4E0] flex items-center justify-between">
              <h2 className="db-h font-semibold">Recent orders</h2>
              <Link to="/admin/orders" className="text-xs text-[#2D6A4F] hover:underline">All orders →</Link>
            </header>
            <div>
              {d.recent_orders.length === 0 ? (
                <div className="p-6 text-center text-[#5C665A] text-sm" data-testid="dashboard-orders-empty">No orders yet.</div>
              ) : d.recent_orders.map((o) => (
                <Link key={o.id} to={`/admin/orders/${o.id}`} className="flex items-center justify-between px-4 py-2 border-b border-[#E2E4E0] last:border-b-0 hover:bg-[#F9F9F8]" data-testid={`dashboard-order-${o.id}`}>
                  <div>
                    <div className="db-mono text-sm font-medium">{o.order_number}</div>
                    <div className="text-xs text-[#5C665A]">{o.buyer_display} · {new Date(o.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-right">
                    <Badge className={o.status === "pending" ? "bg-amber-50 text-amber-800 border-transparent" : "bg-[#E9F5EC] text-[#1B4332] border-transparent"}>{o.status}</Badge>
                    <div className="db-mono text-sm font-semibold mt-1">{fmtEur(o.total || o.subtotal)}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <section className="bg-white border border-[#E2E4E0] rounded-lg">
            <header className="px-4 py-3 border-b border-[#E2E4E0] flex items-center justify-between">
              <h2 className="db-h font-semibold">Low stock</h2>
              <Link to="/admin/stock" className="text-xs text-[#2D6A4F] hover:underline">Stock view →</Link>
            </header>
            <div>
              {d.low_stock_variants.length === 0 ? (
                <div className="p-6 text-center text-[#5C665A] text-sm" data-testid="dashboard-lowstock-empty">All stock healthy.</div>
              ) : d.low_stock_variants.map((v) => (
                <Link key={v.size_variant_id} to="/admin/stock" className="flex items-center justify-between px-4 py-2 border-b border-[#E2E4E0] last:border-b-0 hover:bg-[#F9F9F8]" data-testid={`dashboard-low-${v.size_variant_id}`}>
                  <div>
                    <div className="text-sm font-medium">{v.product_name} <span className="text-[#5C665A]">· {v.size_name}</span></div>
                    <div className="text-xs text-[#5C665A] db-mono">{v.sku}</div>
                  </div>
                  <div className={`db-mono font-semibold ${v.stock_qty <= 0 ? "text-[#C94A4A]" : "text-[#D4A373]"}`}>{v.stock_qty}</div>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

// avoid unused
void Package;
